import { Component, Input, OnChanges } from '@angular/core';
import { Neighborhood } from '../../models/neighborhood.model';

/* -------------------------------------------------------------------------- */
/*                          ANCHOR CONTRATOS LOCALES                          */
/* -------------------------------------------------------------------------- */

/* Las interfaces viven aquí y no en src/app/models porque no describen nada del
   backend: son solo la forma que necesita ESTE template para dejar de repetir
   once bloques casi idénticos. */

/* Pastilla de estado. El estado viaja tres veces a propósito: como clase
   (enabled), como glifo (icon) y como texto en el nombre accesible (ariaLabel).
   Así el color nunca es el único canal de información (WCAG 1.4.1). */
interface StatusChip {
  label: string;
  enabled: boolean;
  icon: string;
  ariaLabel: string;
}

/* Fila etiqueta/valor de los bloques numéricos. `amount` solo se llena cuando el
   valor es dinero: el template lo pinta con el pipe currency en lugar de `value`. */
interface DetailItem {
  label: string;
  value: string;
  amount?: number;
}

/* Par etiqueta/bandera con el que se alimenta buildStatusChips. Se escribe el
   campo del modelo de forma explícita (y no con keyof) para que el compilador
   marque el error si algún día se renombra una propiedad del Neighborhood. */
interface StatusSource {
  label: string;
  enabled: boolean;
}

@Component({
  selector: 'app-neighborhood-item',
  templateUrl: './neighborhood-item.component.html',
  styleUrl: './neighborhood-item.component.scss'
})
export class NeighborhoodItemComponent implements OnChanges {

  @Input() neighborhood?: Neighborhood;

  moduleChips: StatusChip[] = [];
  accessChips: StatusChip[] = [];
  capacityDetails: DetailItem[] = [];
  billingDetails: DetailItem[] = [];
  modulesSummary = '';

  /* Todo se calcula en ngOnChanges y no en getters: la tarjeta se repite una vez
     por colonia dentro de un @for, y un getter se reevaluaría en cada ciclo de
     detección de cambios construyendo arreglos nuevos sin necesidad. */
  ngOnChanges(): void {
    const neighborhood = this.neighborhood;

    if (!neighborhood) {
      this.moduleChips = [];
      this.accessChips = [];
      this.capacityDetails = [];
      this.billingDetails = [];
      this.modulesSummary = '';
      return;
    }

    this.moduleChips = this.buildModuleChips(neighborhood);
    this.accessChips = this.buildAccessChips(neighborhood);
    this.capacityDetails = this.buildCapacityDetails(neighborhood);
    this.billingDetails = this.buildBillingDetails(neighborhood);

    const activeModules = this.moduleChips.filter(
      (chip) => chip.enabled
    ).length;

    this.modulesSummary = `${activeModules} de ${this.moduleChips.length} activos`;
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR MODULOS                                */
  /* -------------------------------------------------------------------------- */

  /* Mismo orden y mismas etiquetas que la pantalla de ajustes de módulos, para
     que quien lea la tarjeta y luego entre a configurarla vea la misma lista.
     helpDeskModule existe en el modelo pero se omite a propósito: no tiene switch
     en ninguna pantalla de la consola, así que mostrarlo expondría un estado que
     nadie puede cambiar desde aquí. */
  private buildModuleChips(neighborhood: Neighborhood): StatusChip[] {
    return this.buildStatusChips([
      { label: 'Llavero digital', enabled: neighborhood.digitalKeychainModule },
      { label: 'Paquetería', enabled: neighborhood.packageModule },
      { label: 'Mascotas', enabled: neighborhood.petModule },
      { label: 'Encuestas', enabled: neighborhood.surveysModule },
      { label: 'Reservaciones', enabled: neighborhood.bookingsModule },
      { label: 'Estado de cuenta', enabled: neighborhood.billingStatementModule },
      { label: 'Chat con vigilancia', enabled: neighborhood.chatModule },
      { label: 'Reportes', enabled: neighborhood.reportsModule },
      { label: 'Amonestaciones', enabled: neighborhood.noticeViolationModule },
      { label: 'IA para visitas', enabled: neighborhood.aiModuleVisits }
    ]);
  }

  /* -------------------------------------------------------------------------- */
  /*                        ANCHOR REQUISITOS DE ACCESO                         */
  /* -------------------------------------------------------------------------- */

  /* Etiquetas cortas: el encabezado de la sección ya dice "Requisitos de acceso",
     repetir "Requiere de..." en cada pastilla solo alarga la línea. */
  private buildAccessChips(neighborhood: Neighborhood): StatusChip[] {
    return this.buildStatusChips([
      { label: 'Pruebas de acceso', enabled: neighborhood.proofsRequired },
      { label: 'Placas', enabled: neighborhood.platesRequired },
      { label: 'Marca', enabled: neighborhood.brandRequired },
      { label: 'Modelo', enabled: neighborhood.modelRequired },
      { label: 'Color', enabled: neighborhood.colorRequired }
    ]);
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR CAPACIDAD                              */
  /* -------------------------------------------------------------------------- */

  /* Propiedades y licencias son dos cosas distintas (así las separa la pantalla
     de ajustes generales) y hasta ahora la tarjeta pintaba numberProperties bajo
     la etiqueta "Número de licencias". Ahora cada una muestra su propio campo. */
  private buildCapacityDetails(neighborhood: Neighborhood): DetailItem[] {
    return [
      {
        label: 'Propiedades',
        value: `${neighborhood.numberProperties ?? 0}`
      },
      {
        label: 'Licencias',
        value: `${neighborhood.licencesNumber ?? 0}`
      },
      {
        label: 'Usuarios por predio',
        value: `${neighborhood.accountsPerProperty ?? 0}`
      },
      {
        label: 'Dispositivos por usuario',
        value: `${neighborhood.devicesPerProperty ?? 0}`
      }
    ];
  }

  /* -------------------------------------------------------------------------- */
  /*                           ANCHOR COBRANZA Y ZONA                           */
  /* -------------------------------------------------------------------------- */

  private buildBillingDetails(neighborhood: Neighborhood): DetailItem[] {
    const maintenanceFee = neighborhood.maintenanceFee ?? 0;
    const payday = neighborhood.payday ?? 0;

    return [
      {
        label: 'Mantenimiento',
        /* Cero se sigue leyendo como "No registrado" y no como "$0": no es una
           cuota de cero pesos, es un dato que nadie ha capturado. */
        value: 'No registrado',
        amount: maintenanceFee === 0
          ? undefined
          : maintenanceFee
      },
      {
        label: 'Día máx. de pago',
        value: payday === 0
          ? 'No registrado'
          : `Día ${payday} de cada mes`
      },
      {
        label: 'Zona horaria',
        value: this.formatTimeZone(neighborhood.timeZone)
      }
    ];
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR UTILIDADES                              */
  /* -------------------------------------------------------------------------- */

  /* El campo guarda solo el desfase (-6, 3...), que suelto no se entiende. Se le
     antepone el prefijo UTC y se añade el signo + cuando es positivo, porque el
     negativo ya viene en el propio número. */
  private formatTimeZone(timeZone?: number): string {
    const offset = timeZone ?? 0;

    if (offset === 0) {
      return 'UTC';
    }

    const sign = offset > 0
      ? '+'
      : '-';

    return `UTC${sign}${Math.abs(offset)}`;
  }

  /* El aria-label se arma aquí para que el lector de pantalla anuncie el estado
     en palabras: el glifo check_circle / remove va oculto con aria-hidden y no
     aporta nombre accesible. */
  private buildStatusChips(sources: StatusSource[]): StatusChip[] {
    return sources.map((source) => ({
      label: source.label,
      enabled: source.enabled,
      icon: source.enabled
        ? 'check_circle'
        : 'remove',
      ariaLabel: source.enabled
        ? `${source.label}: activado`
        : `${source.label}: desactivado`
    }));
  }

}
